import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAdminResult } from '../../api';

const OPTS = ['A', 'B', 'C', 'D'];

export default function AdminTestReview() {
  const { testId } = useParams();
  const { data, isLoading } = useQuery({
    queryKey: ['adminResult', testId],
    queryFn: () => getAdminResult(testId).then(r => r.data),
  });

  if (isLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}><div className="spinner" /></div>;
  if (!data) return null;

  const { student_name, test_title, submitted_at, total_questions, attempted, correct, incorrect, score, max_score, accuracy_pct, attempt_pct, questions } = data;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 20px', fontFamily: 'DM Sans, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontFamily: 'Sora', marginBottom: 4 }}>{test_title}</h1>
          <p style={{ color: '#64748B', fontSize: 14 }}>
            Student: <strong>{student_name}</strong> &nbsp;·&nbsp;
            {submitted_at && new Date(submitted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => window.print()}>Print / Save PDF</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 32 }}>
        {[
          { label: 'Score', value: `${score}/${max_score}`, color: '#4F46E5' },
          { label: 'Attempt %', value: `${Number(attempt_pct).toFixed(1)}%`, color: '#06B6D4' },
          { label: 'Accuracy %', value: `${Number(accuracy_pct).toFixed(1)}%`, color: '#10B981' },
          { label: 'Correct', value: correct, color: '#10B981' },
          { label: 'Incorrect', value: incorrect, color: '#EF4444' },
        ].map(s => (
          <div key={s.label} style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {questions.map((q, i) => (
          <div key={q.question_id} style={{ border: `2px solid ${q.is_correct ? '#10B981' : q.is_attempted ? '#EF4444' : '#E2E8F0'}`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '10px 16px', background: q.is_correct ? '#F0FDF4' : q.is_attempted ? '#FFF5F5' : '#F8FAFC', borderBottom: '1px solid #E2E8F0', display: 'flex', gap: 10 }}>
              <span style={{ fontWeight: 600, color: '#64748B' }}>Q{i + 1}</span>
              <span title="Question ID" style={{ background: '#EEF2FF', color: '#4F46E5', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, fontFamily: 'monospace' }}>QID: {q.question_id}</span>
              <span style={{ fontSize: 12, padding: '2px 10px', borderRadius: 999, fontWeight: 600, background: q.is_correct ? '#D1FAE5' : q.is_attempted ? '#FEE2E2' : '#F1F5F9', color: q.is_correct ? '#065F46' : q.is_attempted ? '#991B1B' : '#64748B' }}>
                {q.is_correct ? 'Correct' : q.is_attempted ? 'Incorrect' : 'Not Attempted'}
              </span>
            </div>
            <div style={{ padding: 16 }}>
              {q.passage_text && (
                <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, lineHeight: 1.8 }}>
                  {q.passage_text}
                </div>
              )}
              <div style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 14, fontWeight: 500 }}>{q.question_text}</div>
              {q.question_image_url && <img src={q.question_image_url} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 12 }} />}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                {OPTS.map(opt => {
                  const val = q[`option_${opt.toLowerCase()}`];
                  const isStudent = q.student_answer === opt;
                  const isCorrect = q.correct_option === opt;
                  let bg = '#F8FAFC', border = '#E2E8F0', color = '#334155';
                  if (isCorrect) { bg = '#F0FDF4'; border = '#10B981'; color = '#065F46'; }
                  if (isStudent && !isCorrect) { bg = '#FFF5F5'; border = '#EF4444'; color = '#991B1B'; }
                  return (
                    <div key={opt} style={{ padding: '10px 12px', borderRadius: 8, background: bg, border: `1.5px solid ${border}` }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <span style={{ fontWeight: 700, color, flexShrink: 0 }}>{opt}.</span>
                        <span style={{ fontSize: 13, color }}>{val}</span>
                        {isCorrect && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#10B981', fontWeight: 700 }}>Correct</span>}
                        {isStudent && !isCorrect && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#EF4444', fontWeight: 700 }}>Marked</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
              {q.explanation && (
                <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '12px 16px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E', marginBottom: 6 }}>EXPLANATION</div>
                  <div style={{ fontSize: 13, lineHeight: 1.8, color: '#78350F' }}>{q.explanation}</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <style>{`@media print { button { display: none !important; } }`}</style>
    </div>
  );
}
